import React, { useEffect, useState } from "react";
import classes from "./RegisterOrders.module.css";
import { Spin } from "antd";
import { ReactComponent as InfoIcon } from "../../assets/info-circle-filled.svg";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { zonesLookup } from "../../lib/cachedLookups";
import { customsCodeKey, fetchCustomsIds } from "../../hooks/useCustomsOptions";
import { useLookupList } from "../../hooks/useLookup";
import { useConfigurationNumber } from "../../hooks/useConfiguration";
import { ConfigurationId } from "../../types/Lookups";
import { ReactComponent as ArrowDown } from "../../assets/arrow-line-Down (2).svg";
import DefaultExcelImport from "../../components/form/DefaultExcelImport";
import api from "../../utils/axios";
import { toast } from "react-toastify";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Typography,
} from "@mui/material";
import useLang from "../../hooks/useLang";
import Map from "../Dashboard/Map";
import useCurrencies from "../../hooks/useCurrencies";
import { CURRENCY_EUR, CURRENCY_LEK } from "../../api/currency";

/**
 * The optional currency column on the bulk-upload sheet.
 *
 * It also appears in `OPTIONAL_COLUMNS` in `DefaultExcelImport`, which is what stops a blank or
 * absent cell being reported as a missing value — the same duplication the other seven optional
 * columns already carry.
 */
const EXCEL_CURRENCY_HEADER = "Valuta";

/**
 * What a seller may write in that cell, lowercased and trimmed first.
 *
 * `EURO` / `LEK` are what the on-screen instructions ask for; the ISO code and the Albanian
 * plural are accepted because sellers hand-type these sheets and both are one keystroke from
 * what they meant. A value that is not on this list is **rejected**, never defaulted — see
 * W-D25 in docs/dual-currency/02-DECISIONS.md.
 */
const EXCEL_CURRENCY_VALUES: Record<string, number> = {
	euro: CURRENCY_EUR,
	eur: CURRENCY_EUR,
	lek: CURRENCY_LEK,
	leke: CURRENCY_LEK,
	lekë: CURRENCY_LEK,
};

const RegisterOrders = () => {
	const { strings } = useLang("registerOrders");
	const {
		dualCurrencyEnabled,
		defaultCurrencyId,
		countryId: deploymentCountryId,
	} = useCurrencies();

	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const zones = useLookupList(zonesLookup);
	const [loading, setLoading] = useState<any>(false);
	const [ordersCountByCity, setOrdersCountByCity] = useState<any[]>([]);
	// The deployment's own country, the same value `Zone/country-id` returns: from the currency
	// list, or from the country setting while that list is loading or if it failed.
	const configuredCountryId = useConfigurationNumber(ConfigurationId.Country);
	const countryId = deploymentCountryId || configuredCountryId || null;
	const [hoveredCity, setHoveredCity] = useState<string>("");
	const [expandedCountryId, setExpandedCountryId] = useState<number | null>(
		null
	);
	const [clickedCity, setClickedCity] = useState<string>("");

	const data = [
		{
			id: 1,
			kolona: strings?.kolona1,
			plotësimi: `\u25CF ${strings?.optional}`,
			lloji: strings?.typeColumn,
			// default: "S",
		},
		{
			id: 1,
			kolona: strings?.kolona2,
			plotësimi: `\u25CF ${strings?.optional}`,
			lloji: strings?.typeColumn,
			// default: "S",
		},
		{
			id: 2,
			kolona: strings?.nameBuyer,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: "Tekst",
			//default: "Po",
		},
		{
			id: 2,
			kolona: strings?.surnameBuyer,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: strings?.text,
			//default: "Po",
		},
		{
			id: 3,
			kolona: strings?.phone,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: strings?.number,
		},
		{
			id: 3,
			kolona: strings?.country,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: strings?.text,
		},
		{
			id: 5,
			kolona: strings?.city,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: strings?.text,
		},
		{
			id: 4,
			kolona: strings?.address,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: strings?.text,
		},
		{
			id: 7,
			kolona: strings?.packageName,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: strings?.text,
		},
		{
			id: 8,
			kolona: strings?.packageDesc,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: strings?.text,
		},
		{
			id: 9,
			kolona: strings?.packageValue,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: strings?.number,
		},
		{
			id: 11,
			kolona: strings?.openable,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: strings?.yesNo,
		},
		{
			id: 12,
			kolona: strings?.fragile,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: strings?.yesNo,
		},
		{
			id: 14,
			kolona: strings?.exchangeable,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: strings?.yesNo,
		},
		{
			id: 14,
			kolona: strings?.kodiDoganor,
			plotësimi: `\u25CF ${strings?.optional}`,
			lloji: "Numër",
			default: "86734034",
		},
		{
			id: 14,
			kolona: strings?.width,
			plotësimi: `\u25CF ${strings?.optional}`,
			lloji: "Numër",
			default: "30 (~cm)",
		},
		{
			id: 14,
			kolona: strings?.length,
			plotësimi: `\u25CF ${strings?.optional}`,
			lloji: "Numër",
			default: "30 (~cm)",
		},
		{
			id: 14,
			kolona: strings?.height,
			plotësimi: `\u25CF ${strings?.optional}`,
			lloji: "Numër",
			default: "30 (~cm)",
		},
		{
			id: 14,
			kolona: strings?.weight,
			plotësimi: `\u25CF ${strings?.optional}`,
			lloji: "Numër",
			default: "0.5 (~kg)",
		},
		{
			id: 14,
			kolona: strings?.invoiceLabel,
			plotësimi: `\u25CF ${strings?.forced}`,
			lloji: strings?.yesNo,
		},
	];

	// Documented only where an order can actually be registered in more than one currency. On
	// Kosovo and Macedonia the API forces any value back to the deployment currency, so a row
	// describing this column would be documenting something that never happens there.
	const currencyRow = {
		id: 14,
		kolona: strings?.currencyColumn,
		plotësimi: `\u25CF ${strings?.optional}`,
		lloji: strings?.currencyType,
		default: strings?.currencyDefault,
	};

	const rows = dualCurrencyEnabled ? [...data, currencyRow] : data;

	useEffect(() => {
		setTableColumns([
			{
				field: "id",
				headerName: "",
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "600",
				}),
				width: 40,
			},
			{
				field: "kolona",
				headerName: strings?.column,
				width: 140,
			},
			{
				field: "plotësimi",
				headerName: strings?.filling,
				cellStyle: (params: any) => ({
					color:
						params.value === "\u25CF Opcionale"
							? "#8E8E8E"
							: params.value === "\u25CF E detyruar"
							? "#4F16C2"
							: "inherit",
				}),
				width: 120,
			},
			{
				field: "lloji",
				headerName: strings?.type,
				width: 120,
			},
			{
				field: "default",
				headerName: strings?.default,
				width: 110,
			},
		]);
		selectOrderCountByCities();
	}, [setTableColumns]);

	const filteredCountry = zones.filter((zone: any) => zone.levelId === 1);

	const getPackageSizeId = (size: any) => {
		const lowercaseSize = size.toLowerCase();
		switch (lowercaseSize) {
			case "xs":
				return 1;
			case "s":
				return 2;
			case "m":
				return 3;
			case "l":
				return 4;
			case "xl":
				return 5;
			default:
				return null;
		}
	};

	const getCountryId = (country: string) => {
		const countryCaseSize = country.trim().toLowerCase();
		switch (countryCaseSize) {
			case "kosovo":
				return 1;
			case "shqiperi":
				return 2;
			case "maqedoni":
				return 3;
			case "kosove":
				return 1;
			case "kosovë":
				return 1;
			case "kosov":
				return 1;
			case "albania":
				return 2;
			case "macedonia":
				return 3;
			default:
				return null;
		}
	};

	/**
	 * The sheet's own currency cell, found case-insensitively.
	 *
	 * `sheet_to_json` keys every row by the header text as it appears in row 1, so a seller who
	 * typed `VALUTA` produces a different key from the template's `Valuta`. Every other column
	 * is matched exactly; this one is not, because it is the only column sellers add by hand to
	 * sheets they already had.
	 */
	const readCurrencyCell = (row: any) => {
		const header = Object.keys(row ?? {}).find(
			(key) =>
				key.trim().toLowerCase() === EXCEL_CURRENCY_HEADER.toLowerCase()
		);
		return header ? row[header] : undefined;
	};

	/**
	 * Resolve one row's registration currency.
	 *
	 * An unrecognised value throws rather than falling back. A silent fallback to the default
	 * on a row that meant EUR misprices the whole order by ~96x, with nothing on screen to say
	 * so — the same reason an unknown city or customs code already rejects the file.
	 *
	 * The destination country has the final say, exactly as it does on the single-order tab: the
	 * local currency is only available on a row staying inside the deployment's own country. A
	 * parcel to Kosovo or Macedonia is settled in EUR and nothing converts between the two
	 * balances, so a LEK figure on one of those rows has no way back.
	 */
	const getOrderCurrencyId = (
		row: any,
		index: number,
		destinationCountryId: number
	) => {
		// Off PostmanAL the column is not offered and the API would force any value back to the
		// deployment currency, so there is nothing to read and nothing to validate.
		if (!dualCurrencyEnabled) {
			return undefined;
		}

		const raw = readCurrencyCell(row);
		const value = String(raw ?? "")
			.trim()
			.toLowerCase();
		const currencyForcedToEur =
			destinationCountryId !== deploymentCountryId;

		// Optional column: blank, or absent entirely, is the deployment's own currency — or EUR
		// where that is the only currency the row may be registered in.
		if (!value) {
			return currencyForcedToEur ? CURRENCY_EUR : defaultCurrencyId;
		}

		const currencyId = EXCEL_CURRENCY_VALUES[value];
		if (!currencyId) {
			throw new Error(
				`${strings?.invalidCurrency} ${
					index + 1
				}: "${raw}". ${strings?.currencyAllowed}`
			);
		}

		// Rejected, not silently corrected to EUR. A seller who typed LEK against a Kosovo row
		// priced that order in LEK in their own head; registering it in EUR at face value would
		// charge the buyer ~96x too much, and dividing the amount by a rate to "fix" it would be a
		// guess. W-D25's reasoning, applied to a value that is recognised but not allowed here.
		if (currencyForcedToEur && currencyId !== CURRENCY_EUR) {
			throw new Error(
				`${strings?.currencyOutsideCountry} ${index + 1}: "${raw}".`
			);
		}
		return currencyId;
	};

	const [isDisable, setIsDisable] = useState(false);

	/**
	 * The ids of the customs codes this file uses, looked up by code (whitespace ignored, as the
	 * import always has). A code the API cannot hold (over 45 characters) is not asked for, so its
	 * row fails as unknown, as before.
	 */
	const lookUpCustomsCodes = (rows: any[]) =>
		fetchCustomsIds(
			Array.from(
				new Set(
					(rows ?? [])
						.map((row) => customsCodeKey(row["Kodi doganor"]))
						.filter((code) => code && code.length <= 45)
				)
			)
		);

	const onExcelImport = async (excelData: any) => {
		try {
			setIsDisable(true);
			setLoading(true);
			const customsIds = await lookUpCustomsCodes(excelData);
			const orders = excelData?.map((row: any, index: any) => {
				const {
					price,
					openable,
					fragile,
					exchangeable,
					customsId,
					countryId,
					city,
					invoice,
					orderCurrencyId,
				} = excelRowValidate(row, index, customsIds);

				return {
					RefId: row.Refid ?? "",
					FirstName: row["Emri i bleresit"],
					LastName: row["Mbiemri i bleresit"],
					Phone: row["Nr. telefonit"],
					CountryId: countryId,
					Address: row["Rruga"],
					AddressDetails: row["Adresa"],
					OrderDescription: row["Emërtimi i pakos"],
					PackageDescription: row["Përshkrimi i pakos"],
					OrderPrice: price,
					OrdersRealPrice: row["Vlera reale e porosisë"]
						? row["Vlera reale e porosisë"]
						: 0,
					Openable: openable,
					Fragile: fragile,
					Exchangeable: exchangeable,
					CustomsId: customsId,
					// CityLabel: row["Qyteti"],
					CityLabel: city,
					Width: row["Gjerësia"] ? row["Gjerësia"] : 20,
					Length: row["Gjatësia"] ? row["Gjatësia"] : 20,
					Height: row["Lartësia"] ? row["Lartësia"] : 20,
					Weight: row["Pesha"] ? row["Pesha"] : 0.5,
					Invoice: invoice,
					// Undefined off PostmanAL, where JSON.stringify drops the key entirely and
					// the API applies the deployment default — exactly what every caller sent
					// before this column existed.
					OrderCurrencyId: orderCurrencyId,
				};
			});
			api.defaults.timeout = 3600 * 1000;
			const response = await api.post("Order/bulk-insert", orders);
			if (response.status === 200) {
				toast.success(strings?.uploadSuccessMessage);
			} else {
				toast.error(strings?.uploadFailureMessage, response.data);
			}
		} catch (error) {
			console.error("Error in onExcelImport:", error);
			toast.error(`${strings?.uploadErrorMessage} ${error}`);
			setIsDisable(false);
		} finally {
			setLoading(false);
			setIsDisable(false);
		}
	};

	const excelRowValidate = (
		row: any,
		index: number,
		customsIds: Map<string, number>
	) => {
		const requiredFields = [
			"Emri i bleresit",
			"Mbiemri i bleresit",
			"Nr. telefonit",
			"Shteti",
			"Qyteti",
			"Rruga",
			"Adresa",
			"Emërtimi i pakos",
			"Përshkrimi i pakos",
			"Vlera e pakos",
			"Hapet",
			"Thyhet",
			"Ndërrim",
			"Fature e firmosur/Flete Kthimi",
		];

		// Check for missing required fields
		for (const field of requiredFields) {
			const value = row[field];
			if (
				value === undefined ||
				value === null ||
				String(value).trim() === ""
			) {
				throw new Error(
					`Missing required field "${field}" at row ${index + 1}`
				);
			}
		}

		// Remove commas and convert price to a number
		const price = parseFloat(
			(row["Vlera e pakos"] || "0").replace(/,/g, "")
		);
		if (isNaN(price) || price < 0) {
			throw new Error(`Invalid price at row ${index + 1}`);
		}

		const realPrice = parseFloat(
			(row["Vlera reale e porosisë"] || "0").replace(/,/g, "")
		);
		if (price === 0 && (isNaN(realPrice) || realPrice <= 0)) {
			throw new Error(
				`OrdersRealPrice is required and must be greater than 0 at row ${
					index + 1
				} when OrderPrice is zero`
			);
		}

		// Boolean fields processing
		const openable = row["Hapet"]?.toLowerCase() === "po" || false;
		const fragile = row["Thyhet"]?.toLowerCase() === "po" || false;
		const exchangeable = row["Ndërrim"]?.toLowerCase() === "po" || false;
		const invoice =
			row["Fature e firmosur/Flete Kthimi"]?.toLowerCase() === "po" ||
			false;
		if (exchangeable && invoice) {
			throw new Error(
				"Porosia mund te jete Nderrim ose Fature e Firmosur/Flete Kthim"
			);
		}
		let customsId = null;
		const customsCode = customsCodeKey(row["Kodi doganor"]);
		if (customsCode) {
			if (!customsIds.has(customsCode)) {
				throw new Error(`Invalid customs code at row ${index + 1}`);
			}
			customsId = customsIds.get(customsCode);
		}
		const country =
			typeof row["Shteti"] === "string"
				? row["Shteti"].trim()
				: row["Shteti"];
		const countryId = getCountryId(country);
		if (!countryId) {
			throw new Error(
				"Shteti should be Kosovo, Shqiperia or Maqedoni at row: " +
					index +
					1
			);
		}
		const cities: string[] = zones
			.filter((zone: any) => zone.parentId === countryId)
			.map((city: any) => city.description);
		const city = row["Qyteti"].trim();
		console.log(city, "city");
		validateCity(city, cities);

		const orderCurrencyId = getOrderCurrencyId(row, index, countryId);

		// Return validated data
		return {
			price,
			openable,
			fragile,
			exchangeable,
			customsId,
			countryId,
			city,
			invoice,
			orderCurrencyId,
		};
	};
	function validateCity(city: string, availableCities: string[]): void {
		if (!availableCities.includes(city)) {
			const closestCity = findClosestCity(city, availableCities);
			throw new Error(`City not found. Did you mean "${closestCity}"?`);
		}
	}

	function findClosestCity(
		inputCity: string,
		availableCities: string[]
	): string {
		let closestCity = availableCities[0];
		let minDistance = levenshteinDistance(inputCity, closestCity);

		for (const city of availableCities) {
			const distance = levenshteinDistance(inputCity, city);
			if (distance < minDistance) {
				closestCity = city;
				minDistance = distance;
			}
		}

		return closestCity;
	}

	function levenshteinDistance(a: string, b: string): number {
		const matrix: number[][] = [];

		// Increment along the first column of each row
		for (let i = 0; i <= b.length; i++) {
			matrix[i] = [i];
		}

		// Increment along the first row of each column
		for (let j = 0; j <= a.length; j++) {
			matrix[0][j] = j;
		}

		// Fill in the rest of the matrix
		for (let i = 1; i <= b.length; i++) {
			for (let j = 1; j <= a.length; j++) {
				if (b.charAt(i - 1) === a.charAt(j - 1)) {
					matrix[i][j] = matrix[i - 1][j - 1];
				} else {
					matrix[i][j] = Math.min(
						matrix[i - 1][j - 1] + 1, // substitution
						matrix[i][j - 1] + 1, // insertion
						matrix[i - 1][j] + 1
					); // deletion
				}
			}
		}

		return matrix[b.length][a.length];
	}

	const excelHeader = {
		Refid: "",
		"Emri i bleresit": "",
		"Mbiemri i bleresit": "",
		"Nr. telefonit": "",
		Shteti: "",
		Qyteti: "",
		Rruga: "",
		Adresa: "",
		"Emërtimi i pakos": "",
		"Përshkrimi i pakos": "",
		"Vlera e pakos": "",
		"Vlera reale e porosisë": "",
		Hapet: "",
		Thyhet: "",
		Ndërrim: "",
		"Kodi doganor": "",
		Gjerësia: "",
		Gjatësia: "",
		Lartësia: "",
		Pesha: "",
		"Fature e firmosur/Flete Kthimi": "",
		// One key, two jobs: it puts the column in the downloaded template AND in the
		// header check the upload runs. Appended last so a sheet saved before this
		// existed keeps its column order.
		//
		// Gated on dualCurrencyEnabled rather than on how many currencies came back —
		// Macedonia's supported set is [DEN, EUR], two entries on a single-currency
		// deployment, so a count check would ship this column there.
		...(dualCurrencyEnabled ? { [EXCEL_CURRENCY_HEADER]: "" } : {}),
	};

	const selectOrderCountByCities = async () => {
		try {
			const response = await api.get("Order/order-count-by-city", {
				params: { userId: null },
			});
			if (response.status === 200) {
				const result = response?.data;
				setOrdersCountByCity(result);
			}
		} catch (error) {
			console.error("Error fetching data:", error);
		}
	};

	const handleCityHover = (countryId: number, cityName: string) => {
		setExpandedCountryId(countryId);
		setHoveredCity(cityName);
	};

	const handleAccordionChange = (countryId: number) => {
		setExpandedCountryId(
			expandedCountryId === countryId ? null : countryId
		);
	};

	const handleCityClick = (cityName: string) => {
		setHoveredCity(cityName);
	};

	return (
		<>
			{loading ? (
				<Spin spinning={loading} tip={strings?.loadingTip} />
			) : (
				<div className={classes.mainHero}>
					<div className={classes.firstCont}>
						<h1 className={classes.title}>
							{strings?.newOrderTitle}
						</h1>
						<div className={classes.mainFirst}>
							<h1 className={classes.titleinfo}>
								{strings?.buyerDataTitle}
							</h1>
							<div className={classes.firstDiv}>
								<DefaultExcelImport
									onExcelImport={(rows: any) =>
										onExcelImport(rows)
									}
									excelHeaders={excelHeader}
									disable={isDisable}
								/>
							</div>
						</div>
						<div className={classes.secondContainer}>
							<div className={classes.generalInfos}>
								<p className={classes.generalTitle}>
									{strings?.generalInfoTitle}
								</p>
								<div className={classes.warningDiv}>
									<div className="w-auto">
										<InfoIcon />
									</div>{" "}
									<div>
										<p className={classes.secondTitle}>
											{strings?.note}
										</p>
										<p className={classes.info}>
											{strings?.attentionInfo}
										</p>
									</div>
								</div>
								<div className={classes.registerInfo}>
									<p>{strings?.firstRowInfo}</p>
								</div>
								<div
									className="ag-theme-alpine"
									style={{
										width: "80%",
										height: 700,
									}}
								>
									<AgGridReact
										rowData={rows}
										columnDefs={tableColumns}
									/>
								</div>
							</div>
							<div className={classes.hartContainer}>
								<p className={classes.hartTitle}>
									{strings?.allowedStatesTitle}
								</p>
								<div className={classes.hart}>
									<h1 className="StatisticTitle">
										{strings?.hart}
									</h1>

									<Map
										ordersCountByCity={ordersCountByCity}
										countryId={countryId}
										onCityHover={handleCityHover}
										hoveredCity={hoveredCity}
									/>
								</div>
								<div>
									{zones
										.filter(
											(zone: any) => zone.levelId === 1
										)
										.map((country: any, index: any) => (
											<Accordion
												key={index}
												className={classes.country}
												expanded={
													expandedCountryId ===
													country.id
												}
												onChange={() =>
													handleAccordionChange(
														country.id
													)
												}
											>
												<AccordionSummary
													expandIcon={<ArrowDown />}
													aria-controls="panel1a-content"
													id="panel1a-header"
												>
													<Typography
														sx={{
															fontWeight: "bold",
														}}
													>
														{country.description}
													</Typography>
												</AccordionSummary>
												<AccordionDetails
													className={classes.cityDiv}
													sx={{ paddingLeft: "15px" }}
												>
													<ul
														className={
															classes.cities
														}
													>
														{zones
															.filter(
																(zone: any) =>
																	zone.parentId ===
																	country.id
															)
															.map(
																(
																	city: any,
																	cityIndex: any
																) => (
																	<li
																		key={
																			cityIndex
																		}
																		style={{
																			color:
																				city.description ===
																				hoveredCity
																					? "#4F16C2"
																					: "inherit",
																		}}
																		onClick={() =>
																			handleCityClick(
																				city.description
																			)
																		}
																	>
																		{
																			city.description
																		}
																	</li>
																)
															)}
													</ul>
												</AccordionDetails>
											</Accordion>
										))}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default RegisterOrders;
